"use client";

import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "~/utils/fontsConfig";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    useTooltip,
} from "~/components/v4/shadcn/Tooltip";

type ProgressSegment = {
    value: number;
    color?: string;
    info?: string;
};

type Props = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    segments: ProgressSegment[];
    emptyColor?: string;
};

const Progress = React.forwardRef<
    React.ElementRef<typeof ProgressPrimitive.Root>,
    Props
>(({ className, segments, emptyColor, ...props }, ref) => {
    const sortedSegments = segments.sort((a, b) => a.value - b.value);

    return (
        <ProgressPrimitive.Root
            ref={ref}
            className={cn(
                "relative h-2 w-full overflow-hidden rounded",
                emptyColor ? emptyColor : "bg-v3-N400",
                className,
            )}
            {...props}
        >
            {sortedSegments.map((segment, index) => (
                <ProgressSegment
                    emptyColor={emptyColor}
                    key={index}
                    segment={segment}
                    index={index}
                    sortedSegments={sortedSegments}
                />
            ))}
        </ProgressPrimitive.Root>
    );
});

const ProgressSegment = ({
    segment,
    index,
    sortedSegments,
    emptyColor,
}: {
    segment: ProgressSegment;
    index: number;
    sortedSegments: ProgressSegment[];
    emptyColor?: string;
}) => {
    const { showTooltip, handleTooltipClick, setShowTooltip } = useTooltip();

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setShowTooltip(!showTooltip);
    };

    return (
        <TooltipProvider>
            <Tooltip
                open={showTooltip}
                onOpenChange={setShowTooltip}
                delayDuration={30}
            >
                <TooltipTrigger asChild>
                    <ProgressPrimitive.Indicator
                        onClick={handleClick}
                        className={cn(
                            "absolute h-full transition-all",
                            segment.color ? segment.color : "bg-v3-N200",
                        )}
                        style={{
                            width: `${segment.value}%`,
                            zIndex: sortedSegments.length - index,
                            left: `${sortedSegments
                                .slice(0, index)
                                .reduce(
                                    (acc, segment) => acc + segment.value,
                                    0,
                                )}%`,
                        }}
                    />
                </TooltipTrigger>
                {segment.info && (
                    <TooltipContent>{segment.info}</TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );
};

Progress.displayName = "Progress";

export { Progress };
